import React from "react";

const ACTIVE = "var(--accent)";
const INACTIVE = "var(--surface-2)";
const STROKE = "var(--border-color)";
const SKIN = "var(--surface-2)";

const muscleProps = (groupKey, target, intensities) => {
  if (intensities) {
    const v = Math.max(0, Math.min(1, Number(intensities[groupKey] || 0)));
    if (v <= 0) return { fill: INACTIVE, opacity: 0.55 };
    if (v < 0.34) return { fill: ACTIVE, opacity: 0.35 };
    if (v < 0.67) return { fill: ACTIVE, opacity: 0.65 };
    return { fill: ACTIVE, opacity: 1 };
  }
  const active =
    groupKey === target ||
    (Array.isArray(target) && target.includes(groupKey));
  return { fill: active ? ACTIVE : INACTIVE, opacity: active ? 1 : 1 };
};

/**
 * Stylized front+back body silhouette.
 *   - <MuscleDiagram group="chest" />            single highlight
 *   - <MuscleDiagram intensities={{chest:0.8}} />  heatmap (each value 0..1)
 *   - <MuscleDiagram onMuscleClick={(m) => ...} /> clicking a muscle fires the callback
 */
export default function MuscleDiagram({
  group = "full_body",
  size = 140,
  intensities = null,
  onMuscleClick = null,
}) {
  const target =
    group === "full_body" || group === "cardio"
      ? ["chest", "back", "shoulders", "biceps", "triceps", "forearms", "core",
         "quads", "hamstrings", "glutes", "calves"]
      : group;

  const Muscle = ({ groupKey, d }) => {
    const baseProps = muscleProps(groupKey, target, intensities);
    if (!onMuscleClick) {
      return <path d={d} {...baseProps} />;
    }
    return (
      <g
        onClick={(e) => { e.stopPropagation(); onMuscleClick(groupKey); }}
        style={{ cursor: "pointer" }}
        data-testid={`muscle-svg-${groupKey}`}
      >
        <path d={d} {...baseProps}>
          <title>{groupKey} — tap for AI micro-workout</title>
        </path>
        {/* Invisible larger hitbox for easier tapping */}
        <path d={d} fill="transparent" stroke="transparent" pointerEvents="all" />
      </g>
    );
  };

  return (
    <div className="flex items-center gap-3" data-testid={`muscle-diagram-${intensities ? "heatmap" : group}`}>
      {/* FRONT VIEW */}
      <svg viewBox="0 0 100 200" width={size / 2} height={size} className="shrink-0">
        <circle cx="50" cy="14" r="9" fill={SKIN} stroke={STROKE} strokeWidth="1" />
        <path
          d="M30 28 Q35 26 50 26 Q65 26 70 28 L74 60 L72 100 L66 130 L60 130 L58 100 L42 100 L40 130 L34 130 L28 100 L26 60 Z"
          fill={SKIN}
          stroke={STROKE}
          strokeWidth="1"
        />
        <Muscle groupKey="shoulders" d="M26 32 Q22 36 21 46 L26 48 L30 36 Z" />
        <Muscle groupKey="shoulders" d="M74 32 Q78 36 79 46 L74 48 L70 36 Z" />
        <Muscle groupKey="chest" d="M32 36 Q40 34 49 36 L49 56 Q40 58 33 54 Z" />
        <Muscle groupKey="chest" d="M68 36 Q60 34 51 36 L51 56 Q60 58 67 54 Z" />
        <Muscle groupKey="biceps" d="M22 48 Q19 60 20 72 L26 72 L28 56 Z" />
        <Muscle groupKey="biceps" d="M78 48 Q81 60 80 72 L74 72 L72 56 Z" />
        <Muscle groupKey="forearms" d="M20 74 Q18 86 19 100 L25 100 L26 76 Z" />
        <Muscle groupKey="forearms" d="M80 74 Q82 86 81 100 L75 100 L74 76 Z" />
        <Muscle groupKey="core" d="M40 60 L60 60 L58 98 L42 98 Z" />
        <line x1="50" y1="62" x2="50" y2="96" stroke={STROKE} strokeWidth="0.5" />
        <line x1="42" y1="74" x2="58" y2="74" stroke={STROKE} strokeWidth="0.5" />
        <line x1="42" y1="84" x2="58" y2="84" stroke={STROKE} strokeWidth="0.5" />
        <Muscle groupKey="quads" d="M34 102 L42 102 L44 150 L36 152 Z" />
        <Muscle groupKey="quads" d="M58 102 L66 102 L64 152 L56 150 Z" />
        <Muscle groupKey="calves" d="M36 154 L44 154 L43 184 L38 184 Z" />
        <Muscle groupKey="calves" d="M56 154 L64 154 L62 184 L57 184 Z" />
      </svg>

      {/* BACK VIEW */}
      <svg viewBox="0 0 100 200" width={size / 2} height={size} className="shrink-0">
        <circle cx="50" cy="14" r="9" fill={SKIN} stroke={STROKE} strokeWidth="1" />
        <path
          d="M30 28 Q35 26 50 26 Q65 26 70 28 L74 60 L72 100 L66 130 L60 130 L58 100 L42 100 L40 130 L34 130 L28 100 L26 60 Z"
          fill={SKIN}
          stroke={STROKE}
          strokeWidth="1"
        />
        <Muscle groupKey="shoulders" d="M26 32 Q22 36 21 46 L26 48 L30 36 Z" />
        <Muscle groupKey="shoulders" d="M74 32 Q78 36 79 46 L74 48 L70 36 Z" />
        <Muscle groupKey="back" d="M32 36 Q40 34 49 36 L49 80 Q40 84 33 80 Z" />
        <Muscle groupKey="back" d="M68 36 Q60 34 51 36 L51 80 Q60 84 67 80 Z" />
        <Muscle groupKey="triceps" d="M22 48 Q19 60 20 72 L26 72 L28 56 Z" />
        <Muscle groupKey="triceps" d="M78 48 Q81 60 80 72 L74 72 L72 56 Z" />
        <Muscle groupKey="forearms" d="M20 74 Q18 86 19 100 L25 100 L26 76 Z" />
        <Muscle groupKey="forearms" d="M80 74 Q82 86 81 100 L75 100 L74 76 Z" />
        <Muscle groupKey="glutes" d="M34 92 Q40 90 49 92 L49 110 Q40 112 33 108 Z" />
        <Muscle groupKey="glutes" d="M68 92 Q60 90 51 92 L51 110 Q60 112 67 108 Z" />
        <Muscle groupKey="hamstrings" d="M34 114 L42 114 L44 150 L36 152 Z" />
        <Muscle groupKey="hamstrings" d="M58 114 L66 114 L64 152 L56 150 Z" />
        <Muscle groupKey="calves" d="M36 154 L44 154 L43 184 L38 184 Z" />
        <Muscle groupKey="calves" d="M56 154 L64 154 L62 184 L57 184 Z" />
      </svg>
    </div>
  );
}
